import { isPlatformBrowser, isPlatformServer, CommonModule } from '@angular/common';
import { Injectable, Inject, PLATFORM_ID, RendererFactory2, ɵɵdefineInjectable, ɵɵinject, Component, ChangeDetectionStrategy, ElementRef, Renderer2, NgZone, Input, Output, EventEmitter, NgModule } from '@angular/core';
import { of, BehaviorSubject, Observable } from 'rxjs';
import { filter, take, map, debounceTime, tap, finalize, switchMap, mapTo } from 'rxjs/operators';
import ResizeObserver from 'resize-observer-polyfill';

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
var Y2PlayerService = /** @class */ (function () {
    function Y2PlayerService(platformId, rendererFactory) {
        this.platformId = platformId;
        this.rendererFactory = rendererFactory;
        this.isLoadApi = false;
        this.loadComplete = new BehaviorSubject(false);
        this._render = this.rendererFactory.createRenderer(null, null);
    }
    /**
     * @return {?}
     */
    Y2PlayerService.prototype.ready = /**
     * @return {?}
     */
    function () {
        return this.loadComplete.pipe(filter((/**
         * @param {?} state
         * @return {?}
         */
        function (state) { return state === true; })), take(1));
    };
    /**
     * @param {?} elm
     * @return {?}
     */
    Y2PlayerService.prototype.loadY2Api = /**
     * @param {?} elm
     * @return {?}
     */
    function (elm) {
        var _this = this;
        return of(null).pipe(map((/**
         * @return {?}
         */
        function () {
            /** @type {?} */
            var id = _this.createVideoId();
            _this._render.setAttribute(elm, 'id', id);
            // if this api is not load, load this api
            if (!_this.isLoadApi) {
                _this.isLoadApi = true;
                /** @type {?} */
                var tag = _this._render.createElement('script');
                _this._render.setAttribute(tag, 'src', 'https://www.youtube.com/player_api');
                /** @type {?} */
                var firstScriptTag = _this._render.selectRootElement('script');
                _this._render.insertBefore(firstScriptTag.parentNode, tag, firstScriptTag);
                if (isPlatformBrowser(_this.platformId)) {
                    window['onYouTubeIframeAPIReady'] = (/**
                     * @return {?}
                     */
                    function () {
                        // console.log('api load!');
                        _this.loadComplete.next(true);
                    });
                }
            }
            return id;
        })));
    };
    /**
     * @private
     * @return {?}
     */
    Y2PlayerService.prototype.createVideoId = /**
     * @private
     * @return {?}
     */
    function () {
        /** @type {?} */
        var len = 7;
        return Math.random().toString(35).substr(2, len);
    };
    Y2PlayerService.decorators = [
        { type: Injectable, args: [{
                    providedIn: 'root'
                },] }
    ];
    /** @nocollapse */
    Y2PlayerService.ctorParameters = function () { return [
        { type: Object, decorators: [{ type: Inject, args: [PLATFORM_ID,] }] },
        { type: RendererFactory2 }
    ]; };
    /** @nocollapse */ Y2PlayerService.ngInjectableDef = ɵɵdefineInjectable({ factory: function Y2PlayerService_Factory() { return new Y2PlayerService(ɵɵinject(PLATFORM_ID), ɵɵinject(RendererFactory2)); }, token: Y2PlayerService, providedIn: "root" });
    return Y2PlayerService;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
/**
 * An observable creator for element resize.
 * @param {?} elm the watch element.
 * @param {?} cb when resize complete, call back function.
 * @param {?=} time resize emit time, default is 200
 * @return {?}
 */
function resizeObservable(elm, cb, time) {
    if (time === void 0) { time = 200; }
    /** @type {?} */
    var elmObserve$;
    return new Observable((/**
     * @param {?} observer
     * @return {?}
     */
    function (observer) {
        elmObserve$ = new ResizeObserver((/**
         * @param {?} entries
         * @param {?} obs
         * @return {?}
         */
        function (entries, obs) {
            observer.next(elmObserve$);
        }));
        elmObserve$.observe(elm);
    })).pipe(debounceTime(time), tap((/**
     * @return {?}
     */
    function () { return cb(); })), finalize((/**
     * @return {?}
     */
    function () {
        elmObserve$.unobserve(elm);
        elmObserve$.disconnect();
    })));
}

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
// https://stackoverflow.com/questions/3452546/how-do-i-get-the-youtube-video-id-from-a-url
/**
 * @param {?} url
 * @return {?}
 */
function getIdFromYoutubeUrl(url) {
    /** @type {?} */
    var regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#\&\?]*).*/;
    /** @type {?} */
    var match = url.match(regExp);
    return (match && match[7].length === 11) ? match[7] : '';
}
/** @type {?} */
var defaultRatio = (9 / 16);
var NgxY2PlayerComponent = /** @class */ (function () {
    function NgxY2PlayerComponent(_y2, _elm, _render, _zoun, platformId) {
        this._y2 = _y2;
        this._elm = _elm;
        this._render = _render;
        this._zoun = _zoun;
        this.platformId = platformId;
        this.onReady = new EventEmitter();
        this.onStateChange = new EventEmitter();
        this.onPlaybackQualityChange = new EventEmitter();
        this.onPlaybackRateChange = new EventEmitter();
        this.onError = new EventEmitter();
        this.onApiChange = new EventEmitter();
        this.initHeight = 0;
    }
    Object.defineProperty(NgxY2PlayerComponent.prototype, "videoUrl", {
        set: /**
         * @param {?} value
         * @return {?}
         */
        function (value) {
            if (value instanceof Array) {
                this.videoId = value.map((/**
                 * @param {?} v
                 * @return {?}
                 */
                function (v) { return getIdFromYoutubeUrl(v); }));
            }
            else {
                this.videoId = getIdFromYoutubeUrl(value);
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(NgxY2PlayerComponent.prototype, "videoId", {
        get: /**
         * @return {?}
         */
        function () {
            return this._videoId;
        },
        set: /**
         * @param {?} value
         * @return {?}
         */
        function (value) {
            this._videoId = value;
            if (this.videoPlayer) {
                if (this.videoPlayer.getPlayerState() === 1 /* PLAYING */) {
                    if (value instanceof Array) {
                        this.videoPlayer.loadPlaylist(value);
                    }
                    else {
                        this.videoPlayer.loadVideoById(value);
                    }
                }
                else {
                    if (value instanceof Array) {
                        this.videoPlayer.cuePlaylist(value);
                    }
                    else {
                        this.videoPlayer.cueVideoById(value);
                    }
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(NgxY2PlayerComponent.prototype, "isAutoSize", {
        get: /**
         * @return {?}
         */
        function () { return this.playerOptions.width === 'auto' || this.playerOptions.height === 'auto'; },
        enumerable: true,
        configurable: true
    });
    /**
     * @return {?}
     */
    NgxY2PlayerComponent.prototype.ngAfterViewInit = /**
     * @return {?}
     */
    function () {
        if (this.containerElm) {
            this.initHeight = this.containerElm.offsetHeight;
        }
        this.setInitStyle();
        if (isPlatformServer(this.platformId)) {
            return;
        }
        this.loadYoutube().subscribe();
    };
    /**
     * @return {?}
     */
    NgxY2PlayerComponent.prototype.ngOnDestroy = /**
     * @return {?}
     */
    function () {
        if (isPlatformServer(this.platformId)) {
            return;
        }
        if (this.resize$) {
            this.resize$.unsubscribe();
        }
        if (this.videoPlayer) {
            this.videoPlayer.destroy();
        }
    };
    /**
     * @private
     * @return {?}
     */
    NgxY2PlayerComponent.prototype.setInitStyle = /**
     * @private
     * @return {?}
     */
    function () {
        if (this.isAutoSize) {
            this.setAutoSize();
        }
        else {
            this._render.setStyle(this._elm.nativeElement, 'width', this.playerOptions.width + "px");
            this._render.setStyle(this._elm.nativeElement, 'height', this.playerOptions.height + "px");
        }
        this._render.setStyle(this._elm.nativeElement, 'background-size', 'cover');
        this._render.setStyle(this._elm.nativeElement, 'background-position', 'center');
        if (this.playerOptions.thumbnail) {
            /** @type {?} */
            var id = this.videoId instanceof Array ? this.videoId[0] : this.videoId;
            this._render.setStyle(this._elm.nativeElement, 'background-image', "url('https://img.youtube.com/vi/" + id + "/" + this.playerOptions.thumbnail + "')");
        }
    };
    /**
     * @private
     * @return {?}
     */
    NgxY2PlayerComponent.prototype.setAutoSize = /**
     * @private
     * @return {?}
     */
    function () {
        if (+this.initHeight !== 0) {
            /** @type {?} */
            var width = this.initHeight / (this.playerOptions.aspectRatio || defaultRatio);
            if (!this.containerElm.offsetWidth || width < this.containerElm.offsetWidth) {
                this._render.setStyle(this._elm.nativeElement, 'height', this.initHeight + "px");
                this._render.setStyle(this._elm.nativeElement, 'width', width + "px");
                return;
            }
        }
        this._render.setStyle(this._elm.nativeElement, 'height', 0);
        this._render.setStyle(this._elm.nativeElement, 'padding-bottom', 100 * (this.playerOptions.aspectRatio || defaultRatio) + "%");
    };
    /**
     * @private
     * @return {?}
     */
    NgxY2PlayerComponent.prototype.loadYoutube = /**
     * @private
     * @return {?}
     */
    function () {
        var _this = this;
        return this._y2.loadY2Api(this._elm.nativeElement).pipe(switchMap((/**
         * @param {?} id
         * @return {?}
         */
        function (id) { return _this._y2.ready().pipe(mapTo(id)); })), map((/**
         * @param {?} id
         * @return {?}
         */
        function (id) {
            var _a;
            /** @type {?} */
            var width;
            /** @type {?} */
            var height;
            if (_this.isAutoSize) {
                (_a = _this.getNowWidthAndHeight(), width = _a.width, height = _a.height);
                _this.resize$ = resizeObservable(_this.containerElm, (/**
                 * @return {?}
                 */
                function () {
                    var _a;
                    if (_this.videoPlayer) {
                        (_a = _this.getNowWidthAndHeight(), width = _a.width, height = _a.height);
                        _this.videoPlayer.setSize(width, height);
                    }
                }), 
                // default time is 200
                (_this.playerOptions.resizeDebounceTime !== undefined ? _this.playerOptions.resizeDebounceTime : 200)).subscribe();
            }
            else {
                width = _this.playerOptions.width;
                height = _this.playerOptions.height;
            }
            return { id: id, width: width, height: height };
        })), tap((/**
         * @param {?} __0
         * @return {?}
         */
        function (_a) {
            var id = _a.id, width = _a.width, height = _a.height;
            /** @type {?} */
            var option = {
                width: width,
                height: height,
                playerVars: _this.playerOptions.playerVars || {}
            };
            if (_this.playerOptions.host) {
                option.host = _this.playerOptions.host;
            }
            if (_this.videoId instanceof Array) {
                option.playerVars.listType = 'player';
            }
            else {
                option.videoId = _this.videoId;
            }
            _this.videoPlayer = new YT.Player(id, option);
            _this.iframeElement = _this.videoPlayer.getIframe();
            _this._render.setStyle(_this.iframeElement, 'background-size', 'cover');
            _this._render.setStyle(_this.iframeElement, 'background-position', 'center');
            _this.checkAddAllYTEvent();
        })));
    };
    /**
     * @private
     * @return {?}
     */
    NgxY2PlayerComponent.prototype.checkAddAllYTEvent = /**
     * @private
     * @return {?}
     */
    function () {
        var _this = this;
        // check ready event
        this.videoPlayer.addEventListener('onReady', (/**
         * @param {?} e
         * @return {?}
         */
        function (e) {
            _this._render.removeStyle(_this.iframeElement, 'padding-bottom');
            _this._render.removeStyle(_this.iframeElement, 'width');
            _this._render.removeStyle(_this.iframeElement, 'height');
            _this._render.removeStyle(_this.iframeElement, 'background-image');
            _this._render.removeStyle(_this.iframeElement, 'background-size');
            _this._render.removeStyle(_this.iframeElement, 'background-position');
            if (_this.onReady.observers.length > 0) {
                // run in zone
                _this._zoun.run((/**
                 * @return {?}
                 */
                function () { return _this.onReady.emit(e); }));
            }
            if (_this.videoId instanceof Array) {
                if (_this.playerOptions.playerVars) {
                    if (_this.playerOptions.playerVars.autoplay) {
                        _this.videoPlayer.loadPlaylist(_this.videoId);
                    }
                    else {
                        _this.videoPlayer.cuePlaylist(_this.videoId);
                    }
                }
            }
        }));
        // check other event
        [
            'onStateChange',
            'onPlaybackQualityChange',
            'onPlaybackRateChange',
            'onError',
            'onApiChange'
        ].forEach((/**
         * @param {?} eventId
         * @return {?}
         */
        function (eventId) {
            // check this output has bind event
            if (_this[eventId].observers.length > 0) {
                // if true, bind event
                _this.videoPlayer.addEventListener(eventId, (/**
                 * @param {?} e
                 * @return {?}
                 */
                function (e) {
                    // run in zone
                    _this._zoun.run((/**
                     * @return {?}
                     */
                    function () { return _this[eventId].emit(e); }));
                }));
            }
        }));
    };
    /**
     * @private
     * @return {?}
     */
    NgxY2PlayerComponent.prototype.getNowWidthAndHeight = /**
     * @private
     * @return {?}
     */
    function () {
        /** @type {?} */
        var aspectRation = this.playerOptions.aspectRatio || defaultRatio;
        /** @type {?} */
        var width = this.containerElm.offsetWidth;
        /** @type {?} */
        var height = width * aspectRation;
        if (this.initHeight !== 0 && height > this.initHeight) {
            height = this.initHeight;
            width = height / aspectRation;
        }
        // when height is bigger than window height
        if (isPlatformBrowser(this.platformId) &&
            height > window.innerHeight) {
            height = window.innerHeight;
            width = window.innerWidth;
        }
        return { width: width, height: height };
    };
    NgxY2PlayerComponent.decorators = [
        { type: Component, args: [{
                    selector: 'ngx-y2-player',
                    template: "",
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    styles: [":host{display:block;width:100%;background:black}"]
                }] }
    ];
    /** @nocollapse */
    NgxY2PlayerComponent.ctorParameters = function () { return [
        { type: Y2PlayerService },
        { type: ElementRef },
        { type: Renderer2 },
        { type: NgZone },
        { type: Object, decorators: [{ type: Inject, args: [PLATFORM_ID,] }] }
    ]; };
    NgxY2PlayerComponent.propDecorators = {
        playerOptions: [{ type: Input, args: ['playerOptions',] }],
        containerElm: [{ type: Input, args: ['container',] }],
        videoUrl: [{ type: Input, args: ['videoUrl',] }],
        videoId: [{ type: Input, args: ['videoId',] }],
        onReady: [{ type: Output, args: ['ready',] }],
        onStateChange: [{ type: Output, args: ['stateChange',] }],
        onPlaybackQualityChange: [{ type: Output, args: ['playbackQualityChange',] }],
        onPlaybackRateChange: [{ type: Output, args: ['playbackRateChange',] }],
        onError: [{ type: Output, args: ['error',] }],
        onApiChange: [{ type: Output, args: ['apiChange',] }]
    };
    return NgxY2PlayerComponent;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
var NgxY2PlayerModule = /** @class */ (function () {
    function NgxY2PlayerModule() {
    }
    NgxY2PlayerModule.decorators = [
        { type: NgModule, args: [{
                    imports: [CommonModule],
                    declarations: [NgxY2PlayerComponent],
                    exports: [NgxY2PlayerComponent]
                },] }
    ];
    return NgxY2PlayerModule;
}());

export { NgxY2PlayerComponent, NgxY2PlayerModule, Y2PlayerService, getIdFromYoutubeUrl };
//# sourceMappingURL=ngx-y2-player.js.map
